import { viewModelRepository } from '@/api/common';
import { autoUpdateHelper } from '@/api/common/auto-update-helper';
import { filterManagerModule } from '@/components/common/filter/filter-manager';
import { smallFilterManagerModule } from '@/components/common/filter/filter-manager/SmallFiltersModule';
import { dataServiceFactory } from '@/definitions/services/data.services';
import { applicationModule } from '@/store/application';
import { localStorageModule } from '@/store/application/local.storage';
import { userStorageModule } from '@/store/application/user.storage';
import { workspaceModule } from '@/store/application/workspace';
import { configModule } from '@/store/config';
import { licenseModule } from '@/store/config/LicenseModule';
import { sortModule } from '@/store/data/SortModule';
import { languageModule } from '@/store/languages';
import { reactive } from 'vue';
import { settingsItemModule } from './SettingsItemModule';

export const DictsUrl = 'dicts.json';

const MaxItemsLimit = 1e4;
const DefaultItemsLimit = 1e2;

export class DataState {
  watchListsModule = viewModelRepository.getWatchListsCachedListViewModel();
  cameraGroupsModule = viewModelRepository.getCameraGroupsCachedListViewModel();
  camerasModule = viewModelRepository.getCamerasCachedListViewModel();
  videosModule = viewModelRepository.getVideosCachedListViewModel();
  currentUserModule = viewModelRepository.getUsersItemViewModel();
  groupsModule = viewModelRepository.getGroupsCachedListViewModel();
  usersModule = viewModelRepository.getUsersCachedListViewModel();
  linesModule = viewModelRepository.getLinesCachedListViewModel();
  casesModule = viewModelRepository.getCasesCachedListViewModel();
  alertsModule = viewModelRepository.getAlertsCachedListViewModel();
  humanCardsModule = viewModelRepository.getCardsHumansListViewModel();
  carCardsModule = viewModelRepository.getCardsCarsListViewModel();

  dicts: Record<string, any> = {};
  reactiveData = reactive({
    ready: false
  });

  constructor() {
    autoUpdateHelper.addListInstance(this.cameraGroupsModule);
    autoUpdateHelper.addListInstance(this.watchListsModule);
    autoUpdateHelper.addListInstance(this.camerasModule);
    autoUpdateHelper.addListInstance(this.videosModule);
    autoUpdateHelper.addListInstance(this.groupsModule);
    autoUpdateHelper.addListInstance(this.usersModule);
    autoUpdateHelper.addListInstance(this.linesModule);
    autoUpdateHelper.addItemInstance(this.currentUserModule);
    autoUpdateHelper.addListInstance(this.alertsModule);
  }

  get watchListsWithoutUnknown() {
    return this.watchListsModule.items.filter((v) => v.id !== -1);
  }

  get editableWatchListsByCurrentUser() {
    const watchListPermissions = this.currentUserModule.item?.watch_list_permissions;
    return this.watchListsModule.items.filter((v) => watchListPermissions?.[String(v.id)] === 'edit');
  }

  get cameraGroupsWithoutVideoGroups() {
    return this.cameraGroupsModule.items.filter((v) => v.id >= 1);
  }

  get isCurrentUserAdmin() {
    let user = this.currentUserModule.item;
    return user && (user.primary_group === 1 || user.groups?.includes(1));
  }

  async load() {
    await this.loadCurrentUserModule();
    this.setLocalStorageUserKey();

    await userStorageModule.getUserData();
    userStorageModule.syncAllFromStorage();
    await smallFilterManagerModule.loadUserFilters();
    await settingsItemModule.get();
    this.setCurrentUserLocale();
    await applicationModule.initTheme();

    await licenseModule.loadLicenses();
    await licenseModule.loadUsageReport();
    await sortModule.load();

    workspaceModule.removeItemsWithoutViewRights();
    await this.loadGroups();
    await this.loadWatchLists();

    if (applicationModule.isMultiProduct) {
      await this.loadCameraGroups();
      await this.loadCameras();
      await this.loadVideos();
      await this.loadLines();
      if (configModule.features.alerts_enabled) await this.loadAlerts();
    } else {
      await this.loadCases();
    }

    await this.loadUsers();

    try {
      await filterManagerModule.getUsersSavedData();
    } catch (e: any) {
      console.warn('[user-me-data] load error', e);
    }
    await this.loadDicts();
    this.reactiveData.ready = true;
  }

  setLocalStorageUserKey() {
    try {
      const newKey = localStorageModule.setKey(this.currentUserModule.item!.name);
      if (newKey) localStorageModule.syncAllFromStorage();
    } catch (e) {
      console.warn('[setLocalStorageUserKey] error ', e);
    }
  }

  async loadCurrentUserModule() {
    await this.currentUserModule.get('me');
  }

  async loadCases() {
    if (!configModule.features.cases_enabled) return;
    try {
      await this.casesModule.get();
    } catch (e: any) {
      console.warn('[cases] load error', e);
    }
  }

  async loadDicts() {
    if (configModule.config.dicts) {
      this.dicts = configModule.config.dicts; //@for-test
      return;
    }

    try {
      await dataServiceFactory
        .getAxiosInstance()
        .get(DictsUrl)
        .then((v) => {
          this.dicts = v.data;
        });
    } catch (e: any) {
      // console.warn('[dicts] load error', e);
    }
  }

  setCurrentUserLocale() {
    const userLocale = this.currentUserModule.item?.language;
    if (userLocale && configModule.canLanguageSelect) {
      languageModule.setLocale(userLocale);
    }
  }

  syncCurrentUserLocale() {
    const userLocale = dataModule.currentUserModule.item!.language;
    if (userLocale === languageModule.locale) return;
    dataModule.currentUserModule.item!.language = languageModule.locale as any;
    dataModule.currentUserModule.save();
  }

  async loadWatchLists() {
    try {
      this.watchListsModule.filter.current.limit = DefaultItemsLimit + '';
      await this.watchListsModule.get();
    } catch (e: any) {
      console.warn('[watch-lists] load error', e);
    }
  }

  async loadCameraGroups() {
    try {
      this.cameraGroupsModule.filter.current.limit = DefaultItemsLimit + '';
      await this.cameraGroupsModule.get();
    } catch (e: any) {
      console.warn('[cg] load error', e);
    }
  }

  async loadCameras() {
    try {
      this.camerasModule.filter.current.limit = DefaultItemsLimit + '';
      await this.camerasModule.get();
    } catch (e: any) {
      console.warn('[cameras] load error', e);
    }
  }

  async loadAlerts() {
    try {
      await this.alertsModule.get();
    } catch (e: any) {
      console.warn('[alerts] load error', e);
    }
  }

  async loadVideos() {
    try {
      await this.videosModule.get();
    } catch (e: any) {
      console.warn('[videos] load error', e);
    }
  }

  async loadGroups() {
    try {
      this.groupsModule.filter.current.limit = MaxItemsLimit + '';
      await this.groupsModule.get();
    } catch (e: any) {
      console.warn('[groups] load error', e);
    }
  }

  async loadUsers() {
    try {
      await this.usersModule.get();
    } catch (e: any) {
      console.warn('[roles] load error', e);
    }
  }

  async loadLines() {
    if (!configModule.features.lines_enabled) return;
    try {
      this.linesModule.filter.current.limit = String(DefaultItemsLimit);
      await this.linesModule.get();
    } catch (e: any) {
      console.warn('[lines] load error', e);
    }
  }

  get cameraGroupsLabels(): string[] {
    const items = this.cameraGroupsModule.items;
    const labelsMap = items.reduce<Record<string, string>>((m, v) => {
      Object.assign(m, v.labels);
      return m;
    }, {});
    return Object.keys(labelsMap);
  }

  async getUserNameById(id: number): Promise<string | undefined> {
    const userName = await dataModule.usersModule.getPropById<'real_name'>(id, 'real_name');
    return userName || String(id);
  }
}

export const dataModule: DataState = new DataState();
// watch(() => globalEventModule.current, handleGlobalEventForDataModule.bind(null, dataModule));
